// src/pages/AnimeDetails.jsx
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-hot-toast'
import { useMediaQuery } from 'react-responsive'

import {
  loadList,
  addItem as addItemThunk,
  updateItem as updateItemThunk,
  removeItem as removeItemThunk,
} from '../../store/slices/watchlist.slice'
import { fetchReviews } from '../../store/slices/review.slice'
import { utilService } from '../../services/util.service'

import { AnimeHeader } from '../../cmps/AnimeDetails/AnimeHeader'
import { AnimeInfo } from '../../cmps/AnimeDetails/AnimeInfo'
import { AnimeContent } from '../../cmps/AnimeDetails/AnimeContent'
import { AnimeHeaderMobile } from '../../cmps/AnimeDetails/AnimeHeaderMobile'
import { AnimeInfoMobile } from '../../cmps/AnimeDetails/AnimeInfoMobile'
import { AnimeCharacterMobile } from '../../cmps/AnimeDetails/AnimeCharacterMobile'
import { AnimeDirectorMobile } from '../../cmps/AnimeDetails/AnimeDirectorMobile'
import { RelatedPost } from '../../cmps/AnimeDetails/RelatedPost'
import { ReviewIndex } from '../../cmps/ReviewSystem/ReviewIndex'
import { AnimeDetailsHelmet } from '../../cmps/Helments/AnimeDetailsHelmet'
import { Loader } from '../../cmps/FarmerMotion/Loader'
import { fetchAnimeById } from '../../store/slices/animeDB.slice'

/* helper: flatten {page:arr} → arr */
const flattenPages = (bucket = {}) => Object.values(bucket).flat()

export function AnimeDetails() {
  const { mal_id } = useParams()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery({ maxWidth: 1024 })
  const [isLoading, setIsLoading] = useState(false)

  /* --- store selectors --- */
  const loggedInUser = useSelector((s) => s.userModule.loggedInUser)
  const anime = useSelector((s) => s.animeDB.selectedAnime)

  /* watch-list buckets של המשתמש (אם קיים) */
  const allBuckets = useSelector(
    (s) => s.watchlist.watchlist[loggedInUser?._id]?.all ?? {}
  )

  /* הפריט העדכני של האנימה מתוך ה-store  (רענן בכל עדכון) */
  const currentItem = useSelector((s) => {
    if (!loggedInUser || !anime) return null
    const pages = s.watchlist.watchlist[loggedInUser._id]?.all ?? {}
    for (const arr of Object.values(pages)) {
      const found = arr.find((it) => it.anizone_id === anime.mal_id)
      if (found) return found
    }
    return null
  })

  const isInWatchlist = !!currentItem

  /* ---------- reviews ---------- */
  const allReviews = useSelector((s) => s.reviewModule.reviews || [])
  const animeReviews = anime
    ? allReviews.filter((r) => r.animeId === anime.mal_id)
    : []

  /* ---------- load anime ---------- */
  useEffect(() => {
    if (!mal_id) return
    setIsLoading(true)
    dispatch(fetchAnimeById(mal_id))
      .unwrap()
      .catch(() => toast.error('שגיאה בטעינת האנימה'))
      .finally(() => setIsLoading(false))
  }, [dispatch, mal_id])

  /* ---------- load watch-list once ---------- */
  useEffect(() => {
    if (!loggedInUser) return
    if (Object.keys(allBuckets).length) return // כבר נטען
    dispatch(
      loadList({
        userId: loggedInUser._id,
        listType: 'watchlist',
        cacheKey: 'all',
        options: { page: 1, limit: 10_000 },
      })
    )
      .unwrap()
      .catch(() => toast.error('שגיאה בטעינת רשימת הצפייה'))
  }, [dispatch, loggedInUser, allBuckets])

  /* ---------- load reviews for this anime ---------- */
  useEffect(() => {
    if (anime?.mal_id) dispatch(fetchReviews({ animeId: anime.mal_id }))
  }, [dispatch, anime?.mal_id])

  /* ---------- helpers ---------- */
  const handleAddItem = (status, item) =>
    utilService.handleAddItem(
      status,
      item,
      dispatch,
      loggedInUser,
      flattenPages(allBuckets),
      addItemThunk
    )

  /* ←–––  הפונקציות ששודרגו  –––→ */
  const onIncrement = (item) => {
    if (!loggedInUser || !item) return
    /* כמה פרקים יש לסדרה?  */
    const maxEps = Number.isFinite(anime?.episodes) ? anime.episodes : Infinity
    const next = Math.min((item.progress ?? 0) + 1, maxEps)

    dispatch(
      updateItemThunk({
        userId: loggedInUser._id,
        listType: 'watchlist',
        anizoneId: item.anizone_id,
        updates: { progress: next },
      })
    )
  }

  const onDecrement = (item) => {
    if (!loggedInUser || !item) return
    const next = Math.max((item.progress ?? 0) - 1, 0)

    dispatch(
      updateItemThunk({
        userId: loggedInUser._id,
        listType: 'watchlist',
        anizoneId: item.anizone_id,
        updates: { progress: next },
      })
    )
  }

  const handleRemove = useCallback(
    (anizoneId) => {
      if (!loggedInUser) return
      dispatch(
        removeItemThunk({
          userId: loggedInUser._id,
          listType: 'watchlist',
          anizoneId,
        })
      )
    },
    [dispatch, loggedInUser]
  )

  const handleSaveChanges = useCallback(
    (updated) => {
      if (!loggedInUser) return
      dispatch(
        updateItemThunk({
          userId: loggedInUser._id,
          listType: 'watchlist',
          anizoneId: updated.anizone_id,
          updates: {
            progress: updated.progress,
            status: updated.status,
            start_date: updated.start_date || null,
            finish_date: updated.finish_date || null,
          },
        })
      )
    },
    [dispatch, loggedInUser]
  )

  /* ---------- render ---------- */
  if (isLoading || !anime) return <Loader />

  return (
    <div className="p-4 text-white container mx-auto">
      <AnimeDetailsHelmet anime={anime} />

      {isMobile ? (
        <div className="flex flex-col gap-7">
          <AnimeHeaderMobile
            anime={anime}
            loggedInUser={loggedInUser}
            dispatch={dispatch}
            handleAddItem={handleAddItem}
            isInWatchlist={isInWatchlist}
            currentWatchItem={currentItem}
            onIncrement={onIncrement}
            onDecrement={onDecrement}
            canEdit
            onRemove={handleRemove}
            onSaveChanges={handleSaveChanges}
          />
          <AnimeInfoMobile
            anime={anime}
            loggedInUser={loggedInUser}
            handleAddItem={handleAddItem}
          />
          <AnimeCharacterMobile anime={anime} />
          <AnimeDirectorMobile anime={anime} />
          <RelatedPost anime={anime} />
        </div>
      ) : (
        <>
          <div>
            <h1 className="text-5xl">
              {anime.title_hebrew || anime.title_english}
            </h1>
            <h3 className="text-xl mb-6">
              {anime.title_english || anime.title}
            </h3>
          </div>

          <div className="grid grid-cols-[15%_55%_25%] justify-between">
            <AnimeHeader
              anime={anime}
              loggedInUser={loggedInUser}
              dispatch={dispatch}
              handleAddItem={handleAddItem}
              isInWatchlist={isInWatchlist}
              currentWatchItem={currentItem}
              onIncrement={onIncrement}
              onDecrement={onDecrement}
              canEdit
              onRemove={handleRemove}
              onSaveChanges={handleSaveChanges}
            />
            <AnimeInfo
              anime={anime}
              loggedInUser={loggedInUser}
              handleAddItem={handleAddItem}
              reviews={animeReviews}
            />
            <AnimeContent anime={anime} />
          </div>
        </>
      )}

      <ReviewIndex animeId={anime.anizone_id} loggedInUser={loggedInUser} />
    </div>
  )
}
